
import { Client, DbClient } from '@/types/entities/client';
import { CreateClientInput, UpdateClientInput } from '@/types/entities';
import { ContactField } from '@/types/contacts';
import type { Json } from '@/types/supabase';

// Helper functions for ContactField <-> Json conversion
export function contactFieldsToJson(contacts: ContactField[] | null): Json {
  if (!contacts) return null;
  return contacts.map(contact => ({
    id: contact.id,
    type: contact.type,
    value: contact.value,
    tag: contact.tag || null,
    hasWhatsapp: contact.hasWhatsapp || false,
    hasTelegram: contact.hasTelegram || false
  }));
}

function jsonToContactFields(json: Json): ContactField[] | null {
  if (!json || !Array.isArray(json)) return null;

  const isRecord = (v: unknown): v is Record<string, unknown> =>
    typeof v === 'object' && v !== null && !Array.isArray(v);

  return (json as unknown[]).map((item: unknown) => {
    const rec = isRecord(item) ? item : {};
    const id = typeof rec.id === 'string' ? rec.id : '';
    const type = typeof rec.type === 'string' ? rec.type : '';
    const value = typeof rec.value === 'string' ? rec.value : '';
    const tag = typeof rec.tag === 'string' ? rec.tag : undefined;
    const hasWhatsapp = typeof rec.hasWhatsapp === 'boolean' ? rec.hasWhatsapp : false;
    const hasTelegram = typeof rec.hasTelegram === 'boolean' ? rec.hasTelegram : false;
    return { id, type, value, tag, hasWhatsapp, hasTelegram } satisfies ContactField;
  });
}

/**
 * Преобразование из БД формата (snake_case) в фронтенд формат (camelCase)
 */
export function dbClientToClient(dbClient: DbClient): Client {
  // Используем full_name напрямую из БД (упрощенная структура)
  const fullName = dbClient.full_name || null;
  
  // Используем company_name как есть, или null если пустое
  const companyName = dbClient.company_name || null;

  return {
    id: dbClient.id,
    userId: dbClient.user_id,
    createdAt: new Date(dbClient.created_at),
    updatedAt: new Date(dbClient.updated_at),
    rentalPointId: dbClient.rental_point_id,
    type: dbClient.type,
    legalEntityType: dbClient.legal_entity_type,
    fullName,
    companyName,
    inn: dbClient.inn,
    kpp: dbClient.kpp,
    phone: dbClient.phone,
    email: dbClient.email,
    address: dbClient.address,
    birthDate: dbClient.birth_date ? new Date(dbClient.birth_date) : null,
    passportSeries: dbClient.passport_series,
    passportNumber: dbClient.passport_number,
    passportIssuedBy: dbClient.passport_issued_by,
    passportIssueDate: dbClient.passport_issue_date ? new Date(dbClient.passport_issue_date) : null,
    bankName: dbClient.bank_name,
    bankAccount: dbClient.bank_account,
    bankBik: dbClient.bank_bik,
    contacts: jsonToContactFields(dbClient.contacts),
    comment: dbClient.comment,
    loyaltyStatus: dbClient.loyalty_status,
    status: dbClient.status,
    registrationDate: new Date(dbClient.registration_date),
    remindBirthday: dbClient.remind_birthday,
    remindDaysBefore: dbClient.remind_days_before,
  };
}

/**
 * Преобразование из фронтенд формата (camelCase) в БД формат (snake_case)
 */
export function clientToDbClient(client: Client): DbClient {
  const now = new Date();
  
  // В упрощенной структуре используем fullName напрямую
  
  return {
    id: client.id,
    user_id: client.userId,
    created_at: client.createdAt ? client.createdAt.toISOString() : now.toISOString(),
    updated_at: client.updatedAt ? client.updatedAt.toISOString() : now.toISOString(),
    rental_point_id: client.rentalPointId,
    type: client.type,
    legal_entity_type: client.legalEntityType,
    // Передаем full_name напрямую (упрощенная структура)
    full_name: client.fullName,
    company_name: client.companyName,
    inn: client.inn,
    kpp: client.kpp,
    phone: client.phone,
    email: client.email,
    address: client.address,
    birth_date: client.birthDate ? client.birthDate.toISOString() : null,
    passport_series: client.passportSeries,
    passport_number: client.passportNumber,
    passport_issued_by: client.passportIssuedBy,
    passport_issue_date: client.passportIssueDate ? client.passportIssueDate.toISOString() : null,
    bank_name: client.bankName,
    bank_account: client.bankAccount,
    bank_bik: client.bankBik,
    contacts: contactFieldsToJson(client.contacts),
    comment: client.comment,
    loyalty_status: client.loyaltyStatus,
    status: client.status,
    registration_date: client.registrationDate ? client.registrationDate.toISOString() : now.toISOString(),
    remind_birthday: client.remindBirthday,
    remind_days_before: client.remindDaysBefore,
  };
}

/**
 * Преобразование CreateClientInput в формат БД
 */
export function createClientInputToDb(input: CreateClientInput): Omit<DbClient, 'id' | 'created_at' | 'updated_at'> {
  // В упрощенной структуре используем full_name напрямую
  
  return {
    user_id: input.user_id,
    rental_point_id: input.rental_point_id,
    type: input.type,
    legal_entity_type: input.legal_entity_type,
    // Передаем full_name напрямую (упрощенная структура)
    full_name: input.full_name,
    company_name: input.company_name,
    inn: input.inn,
    kpp: input.kpp,
    phone: input.phone,
    email: input.email,
    address: input.address,
    birth_date: input.birth_date,
    passport_series: input.passport_series,
    passport_number: input.passport_number,
    passport_issued_by: input.passport_issued_by,
    passport_issue_date: input.passport_issue_date,
    bank_name: input.bank_name,
    bank_account: input.bank_account,
    bank_bik: input.bank_bik,
    contacts: contactFieldsToJson(input.contacts as unknown as ContactField[] | null),
    comment: input.comment,
    loyalty_status: input.loyalty_status,
    status: input.status,
    registration_date: input.registration_date,
    remind_birthday: input.remind_birthday,
    remind_days_before: input.remind_days_before,
  };
}

/**
 * Преобразование Frontend Client в CreateClientInput (camelCase)
 */
export function clientToCreateInput(client: Omit<Client, 'id' | 'createdAt' | 'updatedAt'>): CreateClientInput {
  // В упрощенной структуре используем fullName напрямую
  
  return {
    user_id: client.userId,
    rental_point_id: client.rentalPointId,
    type: client.type,
    legal_entity_type: client.legalEntityType,
    // Передаем full_name напрямую (упрощенная структура)
    full_name: client.fullName,
    company_name: client.companyName,
    inn: client.inn,
    kpp: client.kpp,
    phone: client.phone,
    email: client.email,
    address: client.address,
    birth_date: client.birthDate ? client.birthDate.toISOString() : null,
    passport_series: client.passportSeries,
    passport_number: client.passportNumber,
    passport_issued_by: client.passportIssuedBy,
    passport_issue_date: client.passportIssueDate ? client.passportIssueDate.toISOString() : null,
    bank_name: client.bankName,
    bank_account: client.bankAccount,
    bank_bik: client.bankBik,
    contacts: contactFieldsToJson(client.contacts),
    comment: client.comment,
    loyalty_status: client.loyaltyStatus,
    status: client.status,
    registration_date: client.registrationDate.toISOString(),
    remind_birthday: client.remindBirthday,
    remind_days_before: client.remindDaysBefore,
  };
}

/**
 * Преобразование UpdateClientInput в формат БД
 */
export function updateClientInputToDb(input: UpdateClientInput): Partial<Omit<DbClient, 'id' | 'created_at' | 'user_id'>> {
  const result: Partial<Omit<DbClient, 'id' | 'created_at' | 'user_id'>> = {};
  
  if (input.type !== undefined) result.type = input.type;
  if (input.legal_entity_type !== undefined) result.legal_entity_type = input.legal_entity_type;
  if (input.full_name !== undefined) result.full_name = input.full_name;
  if (input.company_name !== undefined) result.company_name = input.company_name;
  if (input.inn !== undefined) result.inn = input.inn;
  if (input.kpp !== undefined) result.kpp = input.kpp;
  if (input.phone !== undefined) result.phone = input.phone;
  if (input.email !== undefined) result.email = input.email;
  if (input.address !== undefined) result.address = input.address;
  if (input.birth_date !== undefined) result.birth_date = input.birth_date;
  if (input.passport_series !== undefined) result.passport_series = input.passport_series;
  if (input.passport_number !== undefined) result.passport_number = input.passport_number;
  if (input.passport_issued_by !== undefined) result.passport_issued_by = input.passport_issued_by;
  if (input.passport_issue_date !== undefined) result.passport_issue_date = input.passport_issue_date;
  if (input.bank_name !== undefined) result.bank_name = input.bank_name;
  if (input.bank_account !== undefined) result.bank_account = input.bank_account;
  if (input.bank_bik !== undefined) result.bank_bik = input.bank_bik;
  if (input.contacts !== undefined) result.contacts = contactFieldsToJson(input.contacts as unknown as ContactField[] | null);
  if (input.comment !== undefined) result.comment = input.comment;
  if (input.loyalty_status !== undefined) result.loyalty_status = input.loyalty_status;
  if (input.status !== undefined) result.status = input.status;
  if (input.registration_date !== undefined) result.registration_date = input.registration_date;

  // Always update updated_at
  result.updated_at = new Date().toISOString();
  
  return result;
}


